import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useAdminApi, saveBlob } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td } from "@/components/admin/kit";

export const Reports = () => {
  const [rows] = useLoad("/admin/mlm/reports");
  return (
    <div>
      <PageHead title="Sales & Team Reports" />
      <DataTable head={["Distributor", "District", "Status", "Directs", "Team size", "Personal sales", "Team sales"]} testid="reports-table">
        {(rows || []).map((r) => <tr key={r.id}><Td><p className="font-semibold">{r.full_name}</p><p className="text-xs text-slate-500">{r.distributor_id}</p></Td><Td>{r.district}</Td><Td className="capitalize">{r.status}</Td><Td>{r.directs}</Td><Td>{r.team_size}</Td><Td>{inr(r.personal_sales)}</Td><Td>{inr(r.team_sales)}</Td></tr>)}
      </DataTable>
    </div>
  );
};

const TicketRow = ({ t, reload }) => {
  const call = useAdminApi();
  const [msg, setMsg] = useState("");
  const send = async (close) => {
    try {
      await call("post", `/admin/mlm/tickets/${t.id}/reply`, { message: msg, close });
      setMsg("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm" data-testid={`admin-ticket-${t.reference}`}>
      <p className="font-semibold">{t.subject} <span className="text-xs font-normal capitalize text-slate-500">· {t.reference} · {t.name || t.email} {t.distributor_id && `(${t.distributor_id})`} · {t.status}</span></p>
      {t.messages.map((m, i) => <p key={i} className={`mt-2 rounded-lg p-2 ${m.from === "support" ? "bg-amber-50" : "bg-slate-50"}`}><span className="text-xs text-slate-500">{m.from} · {fmtDate(m.at, true)}</span><br />{m.text}</p>)}
      {t.status !== "closed" && <div className="mt-2 flex flex-wrap gap-2"><Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Reply" className="flex-1" /><Button size="sm" disabled={!msg} onClick={() => send(false)}>Reply</Button><Button size="sm" variant="outline" onClick={() => send(true)}>Close</Button></div>}
    </div>
  );
};

export const Support = ({ isAdmin }) => {
  const call = useAdminApi();
  const [tickets, reload] = useLoad("/admin/mlm/tickets");
  const [notes, reloadN] = useLoad("/admin/mlm/notifications");
  const [f, setF] = useState({ title: "", body: "" });
  const send = async () => {
    try {
      await call("post", "/admin/mlm/notifications", f);
      setF({ title: "", body: "" });
      toast.success("Notification sent to all distributors");
      reloadN();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid gap-8 xl:grid-cols-2">
      <div><PageHead title="Support Tickets" /><div className="space-y-3">{(tickets || []).map((t) => <TicketRow key={t.id} t={t} reload={reload} />)}{tickets && !tickets.length && <p className="text-sm text-slate-500">No tickets.</p>}</div></div>
      <div>
        <PageHead title="Notifications" />
        {isAdmin && <div className="mb-4 space-y-2 rounded-2xl border border-slate-200 bg-white p-4"><Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="broadcast-title" /><Textarea placeholder="Message to all distributors" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="broadcast-body" /><Button onClick={send} disabled={f.title.length < 2 || f.body.length < 2} data-testid="broadcast-send">Send to all distributors</Button></div>}
        {(notes || []).map((n) => <div key={n.id} className="border-b border-slate-100 py-2 text-sm"><p className="font-semibold">{n.title}</p><p className="text-slate-600">{n.body}</p><p className="text-xs text-slate-400">{fmtDate(n.created_at, true)}</p></div>)}
      </div>
    </div>
  );
};

export const MlmSettings = () => {
  const call = useAdminApi();
  const [s, reload] = useLoad("/admin/mlm/settings");
  const [f, setF] = useState(null);
  if (!s) return <p className="text-slate-500">Loading…</p>;
  const v = f || { ...s, levels: s.commission_levels.join(", "), min_rupees: s.min_withdrawal / 100 };
  const set = (k, val) => setF({ ...v, [k]: val });
  const save = async () => {
    const levels = v.levels.split(",").map((x) => x.trim()).filter(Boolean).map(Number);
    if (levels.some((x) => Number.isNaN(x))) return toast.error("Commission levels must be numbers separated by commas");
    try {
      await call("put", "/admin/mlm/settings", { package_payments_enabled: v.package_payments_enabled, provider_approval_ref: v.provider_approval_ref, payouts_enabled: v.payouts_enabled, compliance_approval_ref: v.compliance_approval_ref, commission_levels: levels, commission_on_packages: v.commission_on_packages, min_withdrawal: Math.round(Number(v.min_rupees || 0) * 100) });
      toast.success("MLM settings saved");
      setF(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const backup = async () => {
    try {
      saveBlob(await call("get", "/admin/mlm/backup", undefined, { responseType: "blob" }), `bmi-backup-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const Toggle = ({ k, label, testid }) => <label className="flex items-center gap-3 text-sm font-semibold"><Switch checked={!!v[k]} onCheckedChange={(x) => set(k, x)} data-testid={testid} />{label}</label>;
  return (
    <div className="max-w-3xl space-y-6">
      <PageHead title="MLM Settings"><Button variant="outline" onClick={backup} data-testid="download-backup">Download database backup</Button></PageHead>
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
        <Toggle k="package_payments_enabled" label="Enable online package payments (Razorpay)" testid="set-package-payments" />
        <Input placeholder="Payment provider written approval reference (required to enable)" value={v.provider_approval_ref} onChange={(e) => set("provider_approval_ref", e.target.value)} data-testid="set-provider-ref" />
        <p className="text-xs text-slate-500">Keep this OFF until Razorpay has reviewed and approved the distributor business model in writing. Product shop and job application payments are not affected.</p>
      </div>
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
        <p className="font-semibold">Commission rules (verified product sales only)</p>
        <Input placeholder="Level percentages, e.g. 5, 2, 1  (blank = no commission)" value={v.levels} onChange={(e) => set("levels", e.target.value)} data-testid="set-levels" />
        <p className="text-xs text-slate-500">Level 1 = the distributor whose referral link made the sale; Level 2 = their sponsor; and so on. No commission is ever paid for registration or recruitment.</p>
        <Toggle k="commission_on_packages" label="Also calculate commission on paid package (product) purchases" testid="set-on-packages" />
      </div>
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
        <Toggle k="payouts_enabled" label="Enable commission payouts / withdrawals" testid="set-payouts" />
        <Input placeholder="Legal & compliance approval reference (required to enable)" value={v.compliance_approval_ref} onChange={(e) => set("compliance_approval_ref", e.target.value)} data-testid="set-compliance-ref" />
        <label className="block text-xs font-semibold text-slate-500" htmlFor="min-withdrawal">Minimum withdrawal (₹)</label>
        <Input id="min-withdrawal" type="number" value={v.min_rupees} onChange={(e) => set("min_rupees", e.target.value)} data-testid="set-min-withdrawal" />
      </div>
      <Button onClick={save} data-testid="mlm-settings-save">Save settings</Button>
    </div>
  );
};
